/**
 * mix-match.js — Mix & Match Fragrance Picker
 * Custom Element: <mix-match-picker>
 *
 * Renders quantity selectors for each fragrance.
 * Total selected packs must equal the variant's total pack count.
 * Exposes getProperties() for product-page.js to collect line item properties.
 *
 * Data attributes:
 *   data-total-packs  — initial total packs from selected variant
 *
 * Dispatches:
 *   mix-match:valid   — when all packs are assigned (detail: { valid: true })
 *   mix-match:invalid — when packs are incomplete (detail: { valid: false })
 */
/**
 * Default-Prefills für Choice-Architecture (Sprint 018k/m):
 * Bei 4-Pack (3+1) → 2× Ozeanfrische + 2× Duftfrei
 * Bei 8-Pack (5+3) → 4× Ozeanfrische + 4× Duftfrei
 * Frühlingsfrische aktuell ausverkauft, deshalb nicht im Default.
 * Out-of-Stock-Slots werden geskippt (falls sich Verfügbarkeit ändert).
 * User kann die Auswahl jederzeit über +/- ändern.
 */
const DEFAULT_SELECTIONS_BY_TOTAL = {
  4: { 'Ozeanfrische': 2, 'Duftfrei': 2 },
  8: { 'Ozeanfrische': 4, 'Duftfrei': 4 },
};

class MixMatchPicker extends HTMLElement {
  connectedCallback() {
    this.totalPacks = parseInt(this.dataset.totalPacks, 10) || 4;
    this.slots = [...this.querySelectorAll('[data-fragrance]')];
    this.statusEl = this.querySelector('[data-mix-status]');

    if (this.slots.length === 0) return;

    this.hintEl = this.querySelector('[data-mix-match-hint]');
    /* Wird true sobald User manuell +/- klickt. Bundle-Wechsel triggert es NICHT. */
    this.userHasInteracted = false;

    this.bindEvents();
    this.applyDefaultSelection();
    this.updateStatus();
    this.updateHintVisibility();
    this.dispatchValidity();
  }

  /**
   * Prefill default selection wenn Bundle-Größe ein Preset hat UND currentTotal === 0.
   * Idempotent: überschreibt keine existierende User-Auswahl.
   */
  applyDefaultSelection() {
    const preset = DEFAULT_SELECTIONS_BY_TOTAL[this.totalPacks];
    if (!preset) return;
    if (this.getCurrentTotal() > 0) return;

    let applied = 0;
    Object.entries(preset).forEach(([name, qty]) => {
      const slot = this.slots.find((s) => s.dataset.fragrance === name);
      if (!slot) return;
      if (slot.dataset.outOfStock === 'true') return;

      const valueEl = slot.querySelector('[data-mix-value]');
      if (!valueEl) return;

      const cap = this.totalPacks - applied;
      const setQty = Math.min(qty, cap);
      if (setQty <= 0) return;

      valueEl.textContent = setQty;
      applied += setQty;

      const minusBtn = slot.querySelector('[data-mix-minus]');
      if (minusBtn) minusBtn.disabled = false;
    });

    this.updatePlusButtons();
  }

  bindEvents() {
    this.addEventListener('click', (e) => {
      const minusBtn = e.target.closest('[data-mix-minus]');
      const plusBtn = e.target.closest('[data-mix-plus]');

      if (minusBtn) {
        e.preventDefault();
        this.userHasInteracted = true;
        this.changeQty(minusBtn.closest('[data-fragrance]'), -1);
      }

      if (plusBtn) {
        e.preventDefault();
        /* Sold-Out-Check: Plus-Button für ausverkaufte Düfte ignorieren. */
        const slot = plusBtn.closest('[data-fragrance]');
        if (slot?.dataset.outOfStock === 'true') return;
        this.userHasInteracted = true;
        this.changeQty(plusBtn.closest('[data-fragrance]'), 1);
      }
    });
  }

  changeQty(slot, delta) {
    if (!slot) return;

    const valueEl = slot.querySelector('[data-mix-value]');
    if (!valueEl) return;

    const current = parseInt(valueEl.textContent, 10) || 0;
    const newVal = current + delta;

    /* Bounds check: min 0, max = remaining + current */
    if (newVal < 0) return;

    const currentTotal = this.getCurrentTotal();
    const remaining = this.totalPacks - currentTotal;

    if (delta > 0 && remaining <= 0) return;

    /* Sprint 019d #18: Optimistic-UI + INP-Optimierung.
       Zahl SOFORT updaten (< 16ms → fühlt sich instant an), heavy Updates
       (updateStatus, updatePlusButtons, dispatchValidity) via requestAnimationFrame
       im nächsten Paint-Cycle. Auf Low-End-Android reduziert das INP von ~450ms auf ~120ms. */
    valueEl.textContent = newVal;

    /* Sprint 019d #12: Micro-Animation — Zahl bounct kurz nach Klick.
       Class wird nach 200ms wieder entfernt (transition ends). Psychologisches Feedback. */
    valueEl.classList.remove('is-bumped');
    /* Force reflow damit die Animation neu triggert bei rapid clicks */
    void valueEl.offsetWidth;
    valueEl.classList.add('is-bumped');
    if (this._bumpTimer) clearTimeout(this._bumpTimer);
    this._bumpTimer = setTimeout(() => {
      valueEl.classList.remove('is-bumped');
    }, 220);

    /* Minus-Button-State direkt updaten (schneller Visual-Feedback) */
    const minusBtn = slot.querySelector('[data-mix-minus]');
    if (minusBtn) minusBtn.disabled = newVal <= 0;

    /* Sprint 019d #18: Heavy Updates in nächsten Frame verschieben (INP-Fix).
       Wenn User rapid clicks (mehrere in <16ms), coalesceen wir zu einem Update. */
    if (this._updateFrame) cancelAnimationFrame(this._updateFrame);
    this._updateFrame = requestAnimationFrame(() => {
      this.updatePlusButtons();
      this.updateStatus();
      this.updateHintVisibility();
      this.dispatchValidity();
      this._updateFrame = null;
    });
  }

  /**
   * Hint bleibt sichtbar bis User zum ersten Mal manuell +/- klickt.
   * Bundle-Wechsel (setTotalPacks) triggert userHasInteracted NICHT — der Hint
   * überlebt auch Wechsel zwischen 3+1 / 5+3.
   * Bei Bundle-Größen ohne Preset (1-Pack) bleibt der Hint hidden.
   */
  updateHintVisibility() {
    if (!this.hintEl) return;
    const hasPreset = !!DEFAULT_SELECTIONS_BY_TOTAL[this.totalPacks];
    this.hintEl.hidden = this.userHasInteracted || !hasPreset;
  }

  matchesDefaultSelection() {
    const preset = DEFAULT_SELECTIONS_BY_TOTAL[this.totalPacks];
    if (!preset) return false;
    const current = {};
    this.slots.forEach((slot) => {
      const name = slot.dataset.fragrance;
      const val = parseInt(slot.querySelector('[data-mix-value]')?.textContent, 10) || 0;
      if (val > 0 && name) current[name] = val;
    });
    const defaultKeys = Object.keys(preset);
    const currentKeys = Object.keys(current);
    if (defaultKeys.length !== currentKeys.length) return false;
    return defaultKeys.every((k) => current[k] === preset[k]);
  }

  updatePlusButtons() {
    const remaining = this.totalPacks - this.getCurrentTotal();
    this.slots.forEach((slot) => {
      const plusBtn = slot.querySelector('[data-mix-plus]');
      if (plusBtn) plusBtn.disabled = remaining <= 0;
    });
  }

  getCurrentTotal() {
    let total = 0;
    this.slots.forEach((slot) => {
      const valueEl = slot.querySelector('[data-mix-value]');
      if (valueEl) total += parseInt(valueEl.textContent, 10) || 0;
    });
    return total;
  }

  updateStatus() {
    if (!this.statusEl) return;

    const current = this.getCurrentTotal();
    const total = this.totalPacks;
    const isComplete = current === total;

    /* Use pre-rendered translation templates from data attributes */
    if (isComplete) {
      this.statusEl.textContent = this.dataset.tAllSelected
        ? this.dataset.tAllSelected.replace('__TOTAL__', total)
        : `Alle ${total} Packungen gewählt!`;
      this.statusEl.classList.add('product__mix-status--complete');
    } else {
      this.statusEl.textContent = this.dataset.tPacksSelected
        ? this.dataset.tPacksSelected
            .replace('__CURRENT__', current)
            .replace('__TOTAL__', total)
        : `${current} von ${total} Packungen gewählt`;
      this.statusEl.classList.remove('product__mix-status--complete');
    }
  }

  dispatchValidity() {
    const valid = this.getCurrentTotal() === this.totalPacks;
    this.dispatchEvent(
      new CustomEvent(valid ? 'mix-match:valid' : 'mix-match:invalid', {
        bubbles: true,
        detail: { valid },
      })
    );
  }

  /**
   * Update total packs (called by product-page.js when variant changes)
   * Regel: Bundle-Wechsel → wenn neuer Bundle-Größe ein Preset zugeordnet ist,
   * werden die Slots resettet + Preset appliziert (auch wenn User vorher was
   * customized hat). Bundle-Wechsel = neuer Kontext, neuer Default.
   * Wechsel zu Bundle ohne Preset (z.B. 1-Pack) → trim greift wie zuvor.
   */
  setTotalPacks(total) {
    const oldTotal = this.totalPacks;
    this.totalPacks = total;

    const hasNewPreset = !!DEFAULT_SELECTIONS_BY_TOTAL[total];
    const bundleChanged = oldTotal !== total;

    if (bundleChanged && hasNewPreset) {
      /* Neuer Preset verfügbar → sauberer Wechsel: reset + apply */
      this.resetValues();
      this.applyDefaultSelection();
    } else if (total < oldTotal) {
      /* Kein Preset → trim excess (existierende Logic) */
      let currentTotal = this.getCurrentTotal();
      const reversed = [...this.slots].reverse();
      for (const slot of reversed) {
        if (currentTotal <= total) break;
        const valueEl = slot.querySelector('[data-mix-value]');
        if (!valueEl) continue;
        const val = parseInt(valueEl.textContent, 10) || 0;
        if (val > 0) {
          const excess = currentTotal - total;
          const reduce = Math.min(val, excess);
          valueEl.textContent = val - reduce;
          currentTotal -= reduce;

          const minusBtn = slot.querySelector('[data-mix-minus]');
          if (minusBtn) minusBtn.disabled = (val - reduce) <= 0;
        }
      }
    }

    this.updatePlusButtons();
    this.updateStatus();
    this.updateHintVisibility();
    this.dispatchValidity();
  }

  /**
   * Setzt alle Slots stumm auf 0 (ohne Events / Validity-Dispatch).
   * Helper für internen State-Wechsel bei Bundle-Preset-Anwendung.
   */
  resetValues() {
    this.slots.forEach((slot) => {
      const valueEl = slot.querySelector('[data-mix-value]');
      if (valueEl) valueEl.textContent = '0';
      const minusBtn = slot.querySelector('[data-mix-minus]');
      if (minusBtn) minusBtn.disabled = true;
    });
  }

  /**
   * Check if all packs are assigned
   */
  isValid() {
    return this.getCurrentTotal() === this.totalPacks;
  }

  /**
   * Get line item properties for cart/add.js
   * Returns object like { "Duftfrei": "2", "Frühlingsfrische": "1" }
   * Only includes fragrances with qty > 0
   */
  getProperties() {
    const props = {};
    this.slots.forEach((slot) => {
      const name = slot.dataset.fragrance;
      const valueEl = slot.querySelector('[data-mix-value]');
      const qty = parseInt(valueEl?.textContent, 10) || 0;
      if (qty > 0 && name) {
        props[name] = String(qty);
      }
    });
    return props;
  }

  /**
   * Reset all quantities to 0
   */
  reset() {
    this.slots.forEach((slot) => {
      const valueEl = slot.querySelector('[data-mix-value]');
      if (valueEl) valueEl.textContent = '0';

      const minusBtn = slot.querySelector('[data-mix-minus]');
      if (minusBtn) minusBtn.disabled = true;
    });
    this.updatePlusButtons();
    this.updateStatus();
    this.updateHintVisibility();
    this.dispatchValidity();
  }
}

customElements.define('mix-match-picker', MixMatchPicker);
